/**
 * Recursively walks an arbitrary tree of values in pre-order, applying a
 * `visit` function.
 *
 * The `visit` function receives the location of each value in the first two
 * parameters: `parent` and `key`, such that `parent[key]` can be used to get
 * or set the value in place. This applies whether `parent` is an Array, in
 * which case `key` is an integer, or `parent` is a plain Object, and `key` is
 * a string.  The third parameter is the value itself.
 *
 * The `visit` function may make mutations to the value by assigning a new one
 * to `parent[key]`, or if `value` is non-primitive, modifying it directly.
 *
 * The `visit` function returns `false` to prevent further recursion into
 * `value`, or `true` to allow it. If, after `visit` returns, the value is
 * primitive, then there is nothing to recurse into and the return value is
 * ignored.
 */
export function walkValue(
    obj: any,
    visit: (parent: any, key: string | number, value: any) => boolean
): void {
    if (obj == null || typeof obj !== 'object') return;
    if (Array.isArray(obj)) {
        for (let i = 0; i < obj.length; i++) {
            const recurse = visit(obj, i, obj[i]);
            if (recurse) walkValue(obj[i], visit);
        }
        return;
    }
    for (const key of Object.keys(obj)) {
        const recurse = visit(obj, key, obj[key]);
        if (recurse) walkValue(obj[key], visit);
    }
}
